import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import useLocationSearch from "../../../hooks/useLocationSearch";

declare global {
  interface Window {
    google: any;
  }
}

interface StoreFinderProps {
  layout?: "default" | "store-locator";
  variantIds?: string[];
  onStoresFetched?: (stores: any[]) => void;
  onSearch?: (query: string) => void;
}

const StoreFinder = forwardRef<any, StoreFinderProps>(({
  layout = "default",
  variantIds = [],
  onStoresFetched = (_stores: any[]) => {},
  onSearch,
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [userCoords, setUserCoords] = useState(null);
  const [hasManualSearch, setHasManualSearch] = useState(false);

  const {
    handleLocationSearch,
    searchByQuery,
    stores,
    loading,
    userCoords: locUserCoords,
    hasManualSearch: locHasManualSearch,
    errorMessage,
  } = useLocationSearch(inputRef, variantIds);

  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const sessionTokenRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    searchStores: () => {
      if (query.trim()) {
        searchByQuery(query.trim());
        onSearch?.(query.trim());
      }
    }
  }));

  // Notify parent when store list updates
  useEffect(() => {
    onStoresFetched(stores);
  }, [stores]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // console.log(`[StoreFinder] Input changed: "${value}"`);
    setQuery(value);

    // Handle autocomplete predictions based on character length
    const trimmed = value.trim();
    const MIN_CHARS = 4;

    if (trimmed.length >= MIN_CHARS) {
      // Debounce both search and autocomplete predictions
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        // Trigger autocomplete predictions
        if (window.google?.maps && autocompleteServiceRef.current) {
          // console.log(`[Manual Autocomplete] Requesting predictions for: "${trimmed}"`);
          
          autocompleteServiceRef.current.getPlacePredictions(
            {
              input: trimmed,
              types: ['geocode'],
              componentRestrictions: { country: 'AU' },
              sessionToken: sessionTokenRef.current,
              bounds: new window.google.maps.LatLngBounds(
                new window.google.maps.LatLng(-44.0, 113.0),
                new window.google.maps.LatLng(-10.0, 154.0)
              )
            },
            (results: any[], status: any) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                // console.log(`[Manual Autocomplete] Received ${results.length} predictions`);
                setPredictions(results);
                setShowDropdown(true);
                setSelectedIndex(-1);
              } else {
                setPredictions([]);
                setShowDropdown(false);
              }
            }
          );
        }

        // Trigger regular search for store-locator layout
        if (layout === "store-locator") {
          searchByQuery(trimmed, true);
        } else {
          searchByQuery(trimmed);
        }
        onSearch?.(trimmed);
      }, 500);
    } else {
      // Clear predictions and dropdown for short queries
      setPredictions([]);
      setShowDropdown(false);
      
      // Handle empty search
      if (trimmed === "") {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
          if (layout === "store-locator") {
            searchByQuery(trimmed, true);
          } else {
            searchByQuery(trimmed);
          }
          onSearch?.(trimmed);
        }, 300);
      }
    }
  };

  // Handle keyboard navigation for dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && predictions[selectedIndex]) {
          selectPrediction(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setPredictions([]);
        setSelectedIndex(-1);
        break;
    }
  };

  // Function to select a prediction
  const selectPrediction = (prediction: any) => {
    // console.log(`[Manual Autocomplete] Selecting: ${prediction.description}`);
    
    if (placesServiceRef.current && sessionTokenRef.current) {
      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['geometry', 'formatted_address', 'name'],
          sessionToken: sessionTokenRef.current
        },
        (place: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
            const formatted = place.formatted_address || place.name;
            
            // Update input and state
            if (inputRef.current) {
              inputRef.current.value = formatted;
            }
            setQuery(formatted);
            onSearch?.(formatted);
            
            // Trigger search
            if (layout === "store-locator") {
              searchByQuery(formatted, true);
            } else {
              searchByQuery(formatted);
            }

            // Create new session token
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
            
            // Hide dropdown
            setShowDropdown(false);
            setPredictions([]);
          }
        }
      );
    } else {
      // Fallback: use prediction description directly
      const formatted = prediction.description;
      if (inputRef.current) {
        inputRef.current.value = formatted;
      }
      setQuery(formatted);
      onSearch?.(formatted);
      
      if (layout === "store-locator") {
        searchByQuery(formatted, true);
      } else {
        searchByQuery(formatted);
      }
      
      setShowDropdown(false);
      setPredictions([]);
    }
  };

  useEffect(() => {
    if (!window.google?.maps) return;

    // Initialize services once
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    
    const mapDiv = document.createElement('div');
    const map = new window.google.maps.Map(mapDiv);
    placesServiceRef.current = new window.google.maps.places.PlacesService(map);
    
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

    // Hide dropdown when clicking outside
    const handleClickOutside = (e: Event) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleUseMyLocation = () => {
    setQuery("");
    if (inputRef.current) inputRef.current.value = "";
    setHasManualSearch(false);
    setUserCoords(null);

    if (!navigator.geolocation || !window.google?.maps) {
      alert("Geolocation or Google Maps is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(({ coords }) => {
      const latlng = new window.google.maps.LatLng(coords.latitude, coords.longitude);
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formatted = results[0].formatted_address;
          // Removed setting input field value to hide user's location from search bar
          // setQuery(formatted);
          // if (inputRef.current) inputRef.current.value = formatted;

          onSearch?.(formatted);
          searchByQuery(formatted);
        } else {
          console.error("Geocoder failed:", status);
          alert("Unable to determine address from your location.");
        }
      });
    });
  };

  function handleSearch(event: React.MouseEvent<HTMLButtonElement>): void {
    throw new Error("Function not implemented.");
  }

  return (
    <>
      <div
        className={`relative z-20 flex items-center gap-2 ${
          layout === "store-locator" ? "justify-start flex-col lg:flex-row" : ""
        } mb-6`}
      >
        <div className="relative flex items-center w-full border border-[#00263E]">
          {layout !== "store-locator" && (
            <label className="blabel font-medium absolute px-1 left-3 top-0 transform -translate-y-1/2 bg-white text-[#54565A] z-10">
              Enter Suburb or Postcode
            </label>
          )}
          <input
            id="storeFinderInput"
            ref={inputRef}
            autoComplete="off"
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full px-4 py-2 border-none"
            placeholder={layout === "store-locator" ? "Enter Suburb or Postcode" : ""}
          />
          <button
            className="px-4 text-[#00263E] w-auto"
            onClick={handleSearch}
          >
            <i className="icon icon-arrow-right"></i>
          </button>

          {/* Manual Autocomplete Dropdown */}
          {showDropdown && predictions.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-[#00263E] border-t-0 max-h-60 overflow-y-auto z-50 -mx-[1px]">
              {predictions.map((prediction, index) => (
                <div
                  key={prediction.place_id}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0 ${
                    index === selectedIndex ? 'bg-blue-100' : ''
                  }`}
                  onClick={() => {
                    const selectPrediction = (prediction: any) => {
                      // console.log(`[Manual Autocomplete] Selecting: ${prediction.description}`);
                      
                      // We need to access the services from the useEffect scope
                      // This is a simplified approach - in production you might want to move this logic
                      if (inputRef.current) {
                        inputRef.current.value = prediction.description;
                      }
                      setQuery(prediction.description);
                      onSearch?.(prediction.description);
                      
                      // Trigger search with the selected text
                      if (layout === "store-locator") {
                        searchByQuery(prediction.description, true);
                      } else {
                        searchByQuery(prediction.description);
                      }

                      setShowDropdown(false);
                      setPredictions([]);
                    };
                    selectPrediction(prediction);
                  }}
                >
                  <div className="text-sm font-medium text-[#00263E]">
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </div>
                  {prediction.structured_formatting?.secondary_text && (
                    <div className="text-xs text-gray-600">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <span className="bc text-[#54565A]">OR</span>

        <button
          className={`${
            layout === "store-locator" ? "px-4 py-3" : "w-[50px] p-0"
          } bg-[#00263E] text-white flex flex-none items-center gap-2`}
          onClick={handleUseMyLocation}
        >
          <i className="icon icon-location-wide"></i>
          {layout === "store-locator" && "Use My Location"}
        </button>
      </div>

      {errorMessage && (
        <p className="mt-2 text-center text-[#D12727] blabel">{errorMessage}</p>
      )}
    </>
  );
});

export default StoreFinder;